/*
 * Barcode scanner popup (snippets/barcode-scanner.liquid).
 *
 * Ported from the "Ideas Barcode Scanner" app block on gulahmedshop.com. The
 * behaviour is the same — scan with the camera or pick an image, POST the code
 * to the app proxy, follow the product URL it returns — with these changes:
 *   - any [data-barcode-open] element opens it (the app bound exactly two
 *     `.qr-wrapper` nodes and threw if the page had fewer);
 *   - Quagga loads on first open instead of on every page;
 *   - the detection handler is registered once, not on every start.
 */
(() => {
  const dialog = document.getElementById('ia-barcode-dialog');
  if (!dialog || dialog.dataset.ready) return;
  dialog.dataset.ready = 'true';

  const $ = (selector) => dialog.querySelector(selector);
  const viewport = $('[data-barcode-viewport]');
  const result = $('[data-barcode-result]');
  const loader = $('[data-barcode-loader]');
  const actions = $('[data-barcode-actions]');
  const fileInput = $('[data-barcode-file]');

  const READERS = [
    'ean_reader',
    'code_128_reader',
    'ean_8_reader',
    'code_39_reader',
    'codabar_reader',
    'upc_reader',
    'upc_e_reader',
  ];

  let libraryPromise = null;
  let scanning = false;
  let handlerBound = false;

  const setStatus = (text) => {
    result.textContent = text;
  };

  const loadLibrary = () => {
    if (window.Quagga) return Promise.resolve(window.Quagga);
    if (libraryPromise) return libraryPromise;

    libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = dialog.dataset.library;
      script.async = true;
      script.onload = () => (window.Quagga ? resolve(window.Quagga) : reject(new Error('Quagga missing')));
      script.onerror = () => {
        libraryPromise = null;
        reject(new Error('Quagga failed to load'));
      };
      document.head.appendChild(script);
    });

    return libraryPromise;
  };

  const lookup = async (code) => {
    setStatus('Barcode Found. Searching...');
    loader.hidden = false;

    try {
      const response = await fetch(window.location.origin + dialog.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: window.location.origin, code }),
      });
      const data = await response.json();

      if (data && data.status && data.url) {
        setStatus('Redirecting to product...');
        window.location.href = data.url;
        return;
      }

      setStatus('Product Not Found');
    } catch (error) {
      setStatus('API call failed. Please try again.');
    } finally {
      loader.hidden = true;
    }
  };

  const stopScanner = () => {
    if (!scanning) return;
    scanning = false;
    if (window.Quagga) window.Quagga.stop();
    viewport.hidden = true;
    viewport.replaceChildren();
    actions.hidden = false;
  };

  const startScanner = async () => {
    if (scanning) return;
    scanning = true;
    setStatus('Loading camera...');

    let Quagga;
    try {
      Quagga = await loadLibrary();
    } catch (error) {
      scanning = false;
      setStatus('Error: Could not start scanner.');
      return;
    }

    if (!handlerBound) {
      handlerBound = true;
      Quagga.onDetected((data) => {
        if (!scanning || !data || !data.codeResult) return;
        const code = data.codeResult.code;
        stopScanner();
        lookup(code);
      });
    }

    viewport.hidden = false;

    Quagga.init(
      {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: viewport,
          constraints: { facingMode: 'environment' },
        },
        decoder: { readers: READERS },
      },
      (error) => {
        if (error) {
          scanning = false;
          viewport.hidden = true;
          setStatus(
            error.name === 'NotAllowedError' ? 'Error: Camera access denied.' : 'Error: Could not start scanner.'
          );
          return;
        }

        // The popup may have been closed while the camera was starting.
        if (!scanning) {
          Quagga.stop();
          return;
        }

        Quagga.start();
        actions.hidden = true;
        setStatus('Scanning...');
      }
    );
  };

  const scanImage = async (src) => {
    setStatus('Scanning image...');

    let Quagga;
    try {
      Quagga = await loadLibrary();
    } catch (error) {
      setStatus('Error: Could not start scanner.');
      return;
    }

    Quagga.decodeSingle({ decoder: { readers: READERS }, locate: true, src }, (decoded) => {
      if (decoded && decoded.codeResult) {
        lookup(decoded.codeResult.code);
      } else {
        setStatus('No barcode detected in the image.');
      }
    });
  };

  const open = () => {
    setStatus('');
    loader.hidden = true;
    actions.hidden = false;
    if (!dialog.open) dialog.showModal();
    // Warm the library while the shopper decides.
    loadLibrary().catch(() => {});
  };

  const close = () => {
    stopScanner();
    if (dialog.open) dialog.close();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-barcode-open]');
    if (!trigger) return;
    event.preventDefault();
    open();
  });

  $('[data-barcode-close]').addEventListener('click', close);
  $('[data-barcode-start]').addEventListener('click', startScanner);
  $('[data-barcode-browse]').addEventListener('click', () => fileInput.click());

  // A click that lands on the dialog element itself is a click on the backdrop.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  // Esc closes the dialog natively; make sure the camera goes with it.
  dialog.addEventListener('close', stopScanner);

  fileInput.addEventListener('change', () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => scanImage(reader.result);
    reader.readAsDataURL(file);
    fileInput.value = '';
  });
})();
